/**
 * MessagingSignalRService — unified `/messagingHub` client.
 *
 * Replaces `ChatSignalRService` (1:1) and the room-chat surface on
 * `SignalRService` (room) once `messaging.persistence_v2` flips on.
 * Until then, both legacy services continue to serve traffic and this
 * client is dormant.
 *
 * Reconnect reconciliation (AC-01.2): on reconnect the client calls
 * `JoinConversation` with its `lastKnownMessageId`; the server replies
 * with `ConversationSync { messages: [missing...], hasMore }` so the
 * client only fetches the gap.
 */

import * as signalR from '@microsoft/signalr';
import { apiConfig } from '../../config/api';
import type {
  ConversationSync,
  ConversationType,
  MessageExpiredEvent,
  SendMessageV2Request,
  UnifiedMessage,
  UnifiedMessageResult,
  UnifiedMessagesReadEvent,
  UnifiedTypingIndicator,
} from '../../types/messaging';
import type {
  ConnectionInvite,
  ConnectionPresence,
  PresenceStatus,
} from '../../types/social';

export type MessagingConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

export interface MessagingEventHandlers {
  onConnectionStateChange?: (state: MessagingConnectionState) => void;
  onReceiveMessage?: (message: UnifiedMessage) => void;
  onMessageDeleted?: (messageId: string) => void;
  onMessageExpired?: (event: MessageExpiredEvent) => void;
  onMessagesRead?: (event: UnifiedMessagesReadEvent) => void;
  onTypingIndicator?: (indicator: UnifiedTypingIndicator) => void;
  onConversationSync?: (sync: ConversationSync) => void;
  // Social presence + invites (social.dm_presence / social.invites_feed). These
  // ride the same hub connection; server emits presence only to a user's
  // accepted, non-blocked connections and never emits an actor's own
  // 'invisible' status to others.
  onReceivePresence?: (presence: ConnectionPresence) => void;
  onReceiveInvite?: (invite: ConnectionInvite) => void;
  onInviteExpired?: (inviteId: string) => void;
  onError?: (error: Error) => void;
}

interface JoinedConversation {
  conversationType: ConversationType;
  conversationId: string;
  lastKnownMessageId?: string;
}

const HUB_PATH = '/messagingHub';

export class MessagingSignalRService {
  private connection: signalR.HubConnection | null = null;
  private state: MessagingConnectionState = 'disconnected';
  private handlers: MessagingEventHandlers = {};
  private joined: Map<string, JoinedConversation> = new Map();
  // Token is held only as a factory closure, never on the instance, so
  // a `(service as any).accessToken` cast can't read it back out.
  private accessTokenFactory: (() => string | Promise<string>) | null = null;
  private profileId: string | null = null;

  get connectionState(): MessagingConnectionState {
    return this.state;
  }

  get isConnected(): boolean {
    return this.state === 'connected';
  }

  setEventHandlers(handlers: MessagingEventHandlers): void {
    this.handlers = { ...this.handlers, ...handlers };
  }

  async connect(
    accessTokenOrFactory: string | (() => string | Promise<string>),
    profileId: string
  ): Promise<void> {
    if (this.state === 'connected' || this.state === 'connecting') return;

    this.accessTokenFactory =
      typeof accessTokenOrFactory === 'function'
        ? accessTokenOrFactory
        : () => accessTokenOrFactory;
    this.profileId = profileId;
    this.setState('connecting');

    const factory = this.accessTokenFactory;
    const url = `${apiConfig.baseUrl}${HUB_PATH}`;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => Promise.resolve(factory()),
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (ctx) => {
          const delays = [0, 1_000, 2_000, 5_000, 10_000, 30_000];
          return delays[Math.min(ctx.previousRetryCount, delays.length - 1)];
        },
      })
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.connection = connection;
    this.wireHubEvents(connection);

    try {
      await connection.start();
      this.setState('connected');
      // Re-join everything we were in.
      for (const conv of this.joined.values()) {
        await this.invokeJoin(conv);
      }
    } catch (err) {
      this.setState('error');
      this.handlers.onError?.(err as Error);
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.stop();
    } finally {
      this.connection = null;
      this.joined.clear();
      this.accessTokenFactory = null;
      this.setState('disconnected');
    }
  }

  /**
   * Join a conversation. Server replies with `ConversationSync` carrying
   * any messages newer than `lastKnownMessageId`.
   */
  async joinConversation(
    conversationType: ConversationType,
    conversationId: string,
    lastKnownMessageId?: string
  ): Promise<void> {
    const key = this.keyFor(conversationType, conversationId);
    const conv: JoinedConversation = { conversationType, conversationId, lastKnownMessageId };
    this.joined.set(key, conv);
    if (this.isConnected) await this.invokeJoin(conv);
  }

  async leaveConversation(
    conversationType: ConversationType,
    conversationId: string
  ): Promise<void> {
    const key = this.keyFor(conversationType, conversationId);
    this.joined.delete(key);
    if (!this.connection || !this.isConnected) return;
    try {
      await this.connection.invoke('LeaveConversation', conversationType, conversationId);
    } catch (err) {
      console.warn('[MessagingSignalR] LeaveConversation failed', err);
    }
  }

  /**
   * Send via the hub. The hub is idempotent on `clientMessageId` so
   * retries are safe. Falls back to REST if the hub is down — caller
   * decides via the outbox queue.
   */
  async sendMessage(req: SendMessageV2Request): Promise<UnifiedMessageResult> {
    if (!this.connection || !this.isConnected) {
      return { success: false, error: 'Not connected' };
    }
    try {
      const result = await this.connection.invoke<UnifiedMessageResult>('SendMessage', req);
      // Track lastKnownMessageId for reconnect reconciliation.
      if (result.success && result.message) {
        const key = this.keyFor(req.conversationType, req.conversationId);
        const conv = this.joined.get(key);
        if (conv) conv.lastKnownMessageId = result.message.id;
      }
      return result;
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  }

  async markRead(messageId: string): Promise<void> {
    if (!this.connection || !this.isConnected) return;
    try {
      await this.connection.invoke('MarkRead', messageId);
    } catch (err) {
      console.warn('[MessagingSignalR] MarkRead failed', err);
    }
  }

  async sendTypingIndicator(
    conversationType: ConversationType,
    conversationId: string,
    isTyping: boolean
  ): Promise<void> {
    if (!this.connection || !this.isConnected) return;
    try {
      await this.connection.invoke('TypingIndicator', conversationType, conversationId, isTyping);
    } catch {
      // typing is best-effort
    }
  }

  /**
   * Broadcast a presence change to the caller's connections (social.dm_presence).
   * Best-effort — presence is not critical-path.
   */
  async updatePresence(status: PresenceStatus): Promise<void> {
    if (!this.connection || !this.isConnected) return;
    try {
      await this.connection.invoke('UpdatePresence', status);
    } catch (err) {
      console.warn('[MessagingSignalR] UpdatePresence failed', err);
    }
  }

  /**
   * Opt in/out of invisible mode. While invisible the server never emits the
   * caller's presence to anyone; the caller still receives others'.
   */
  async setInvisible(invisible: boolean): Promise<void> {
    if (!this.connection || !this.isConnected) return;
    try {
      await this.connection.invoke('SetInvisible', invisible);
    } catch (err) {
      console.warn('[MessagingSignalR] SetInvisible failed', err);
    }
  }

  // -------------------------------------------------------------------------

  private async invokeJoin(conv: JoinedConversation): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.invoke(
        'JoinConversation',
        conv.conversationType,
        conv.conversationId,
        conv.lastKnownMessageId ?? null
      );
    } catch (err) {
      console.warn('[MessagingSignalR] JoinConversation failed', err);
    }
  }

  private wireHubEvents(connection: signalR.HubConnection): void {
    connection.on('ReceiveMessage', (message: UnifiedMessage) => {
      // Track the latest id for reconnect reconciliation.
      const key = this.keyFor(message.conversationType, message.conversationId);
      const conv = this.joined.get(key);
      if (conv) conv.lastKnownMessageId = message.id;
      this.handlers.onReceiveMessage?.(message);
    });

    connection.on('MessageDeleted', (messageId: string) => {
      this.handlers.onMessageDeleted?.(messageId);
    });

    connection.on('MessageExpired', (event: MessageExpiredEvent) => {
      this.handlers.onMessageExpired?.(event);
    });

    connection.on('MessagesRead', (event: UnifiedMessagesReadEvent) => {
      this.handlers.onMessagesRead?.(event);
    });

    connection.on('TypingIndicatorReceived', (indicator: UnifiedTypingIndicator) => {
      this.handlers.onTypingIndicator?.(indicator);
    });

    connection.on('ConversationSync', (sync: ConversationSync) => {
      const key = this.keyFor(sync.conversationType, sync.conversationId);
      const conv = this.joined.get(key);
      if (conv && sync.messages.length > 0) {
        conv.lastKnownMessageId = sync.messages[sync.messages.length - 1].id;
      }
      this.handlers.onConversationSync?.(sync);
    });

    // Social presence + invites ride the same hub. Server has already scoped
    // presence to the caller's connections and stripped invisible actors.
    connection.on('ReceivePresence', (presence: ConnectionPresence) => {
      this.handlers.onReceivePresence?.(presence);
    });

    connection.on('ReceiveInvite', (invite: ConnectionInvite) => {
      this.handlers.onReceiveInvite?.(invite);
    });

    connection.on('InviteExpired', (inviteId: string) => {
      this.handlers.onInviteExpired?.(inviteId);
    });

    connection.onreconnecting(() => this.setState('reconnecting'));
    connection.onreconnected(async () => {
      this.setState('connected');
      // Re-issue Join for each tracked conversation so the server can
      // send a fresh ConversationSync covering the gap.
      for (const conv of this.joined.values()) {
        await this.invokeJoin(conv);
      }
    });
    connection.onclose((err) => {
      this.setState('disconnected');
      if (err) this.handlers.onError?.(err);
    });
  }

  private setState(s: MessagingConnectionState): void {
    this.state = s;
    this.handlers.onConnectionStateChange?.(s);
  }

  private keyFor(type: ConversationType, id: string): string {
    return `${type}:${id}`;
  }

  /** Diagnostics — never use for control flow. Never exposes the token. */
  get debugInfo() {
    return {
      hasToken: this.accessTokenFactory != null,
      profileId: this.profileId,
      joined: Array.from(this.joined.keys()),
    };
  }
}

export const messagingSignalRService = new MessagingSignalRService();
